import { Container, Typography, Grid, Box, IconButton, Button } from "@mui/material";
import { useState, useEffect } from "react";
import { Add } from "@mui/icons-material";

import CertificationCard from "../components/CertificationCard";
import EditDialog from "../components/EditDialog";
import { useEditMode } from "../context/EditModeContext";
import ConfirmDeleteDialog from "./Timeline/ConfirmDeleteDialog";
import ImportJsonDialog from "./Timeline/ImportJsonDialog";
import { useSupabaseTable } from "../hooks/useSupabaseTable";
import certificationsData from "../data/certifications.json";

export type CertificationItem = {
  name: string;
  issuer: string;
  year: string;
};

export default function Certifications() {
  const { isEditMode } = useEditMode();

  const [editing, setEditing] = useState<CertificationItem | null>(null);
  const [isNew, setIsNew] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<CertificationItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  const { data: supabaseItems, insert, update, remove } = useSupabaseTable<CertificationItem>("certification", { realtime: true });
  const [items, setItems] = useState<CertificationItem[]>([]);

  useEffect(() => {
    if (supabaseItems) setItems(supabaseItems);
  }, [supabaseItems]);

  const handleSave = async (updated: CertificationItem) => {
    try {
      if (isNew) {
        await insert(updated);
      } else {
        await update(updated, { name: editing?.name });
      }
      setEditing(null);
      setIsNew(false);
    } catch (err) {
      console.error("Error saving certification:", err);
    }
  };

  const handleAdd = () => {
    setEditing({ name: "", issuer: "", year: "" });
    setIsNew(true);
  };

  const handleDeleteClick = (item: CertificationItem) => {
    setDeleteTarget(item);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove({ name: deleteTarget.name });
      setDeleteTarget(null);
      setConfirmOpen(false);
    } catch (err) {
      console.error("Error deleting certification:", err);
    }
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setConfirmOpen(false);
  };

  const importJson = async (jsonData: CertificationItem[]) => {
    try {
      for (const item of jsonData) {
        await insert(item);
      }
    } catch (err) {
      console.error("Error importing certifications:", err);
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Certifications</Typography>
        {isEditMode && (
          <Box>
            <IconButton onClick={handleAdd} color="primary">
              <Add />
            </IconButton>
            <Button variant="outlined" onClick={() => setJsonDialogOpen(true)}>
              Import JSON
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        {items.map((cert, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
            <CertificationCard
              {...cert}
              onEdit={() => setEditing(cert)}
              onDelete={() => handleDeleteClick(cert)}
            />
          </Grid>
        ))}
      </Grid>

      {editing && (
        <EditDialog
          open={!!editing}
          item={editing}
          onSave={handleSave}
          onClose={() => {
            setEditing(null);
            setIsNew(false);
          }}
        />
      )}

      {/* Import JSON Dialog */}
      <ImportJsonDialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        onSubmit={importJson}
        exampleJson={JSON.stringify(certificationsData, null, 2)}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDeleteDialog<CertificationItem>
        open={confirmOpen}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        target={deleteTarget}
        getDisplayText={(item) => `${item.name} - ${item.issuer} (${item.year})`}
      />

    </Container>
  );
}
